import React from "react";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
}

const Pagination: React.FC<PaginationProps> = ({ 
  currentPage, 
  totalPages, 
  onPageChange, 
  isLoading = false 
}) => {
  const handlePrevPage = () => {
    if (currentPage > 1 && !isLoading) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages && !isLoading) {
      onPageChange(currentPage + 1);
    }
  };

  const renderPageNumbers = () => {
    if (isLoading) {
      return <span className="px-2 text-[12px] text-gray-500">Loading...</span>;
    }

    const pages = [];
    const pageRange = 2;

    // Add first page with ellipsis if needed
    if (currentPage > pageRange + 1) {
      pages.push(
        <span
          key='first'
          onClick={() => !isLoading && onPageChange(1)}
          className='cursor-pointer text-[12px] px-2 hover:text-blue-600 transition-colors'
        >
          1
        </span>
      );
      if (currentPage > pageRange + 2) {
        pages.push(
          <span key='start-ellipsis' className="text-[12px] px-1">
            ...
          </span>
        );
      }
    }

    // Add page numbers around current page
    for (
      let i = Math.max(1, currentPage - pageRange);
      i <= Math.min(totalPages, currentPage + pageRange);
      i++
    ) {
      pages.push(
        <span
          key={i}
          onClick={() => !isLoading && onPageChange(i)}
          className={`cursor-pointer text-[12px] px-3 py-1 rounded ${
            currentPage === i 
              ? "bg-white shadow-md text-black font-medium" 
              : "hover:text-blue-600 hover:bg-gray-100 transition-colors"
          } ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          {i}
        </span>
      );
    }

    // Add last page with ellipsis if needed
    if (currentPage < totalPages - pageRange) {
      if (currentPage < totalPages - pageRange - 1) {
        pages.push(
          <span key='end-ellipsis' className="text-[12px] px-1">
            ...
          </span>
        );
      }
      pages.push(
        <span
          key='last'
          onClick={() => !isLoading && onPageChange(totalPages)}
          className='cursor-pointer text-[12px] px-2 hover:text-blue-600 transition-colors'
        >
          {totalPages}
        </span>
      );
    }

    return pages;
  };

  return (
    <div className='pagination max-w-lg mx-auto flex justify-between items-center py-6'>
      <button
        className={`flex items-center justify-center gap-2 px-4 bg-white shadow py-2 text-[12px] font-medium rounded-lg border ${
          currentPage === 1 || isLoading 
            ? "opacity-50 cursor-not-allowed text-gray-400 border-gray-300" 
            : "text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400 transition-colors"
        }`}
        onClick={handlePrevPage}
        disabled={currentPage === 1 || isLoading}
      >
        Prev
      </button>

      <div className='flex items-center gap-1'>
        {renderPageNumbers()}
      </div>
      
      <button
        className={`flex items-center justify-center gap-2 bg-white shadow px-4 py-2 text-[12px] font-medium rounded-lg border ${
          currentPage === totalPages || isLoading 
            ? "opacity-50 cursor-not-allowed text-gray-400 border-gray-300" 
            : "text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400 transition-colors"
        }`}
        onClick={handleNextPage}
        disabled={currentPage === totalPages || isLoading}
      >
        Next
      </button>
    </div>
  );
};

export default Pagination;